import { useRef, useState, useMemo, useEffect, useCallback, createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import type { GridApi, ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { useQuery, useMutation } from 'convex/react';
import { format } from 'date-fns';
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RotateCcw,
  Loader2,
  ArrowRightLeft,
  ParkingSquare,
  ExternalLink,
  Pencil,
  Check,
  X,
  ListTodo,
  Copy,
  Filter,
  ShieldCheck,
  FileSpreadsheet,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';
import { utils, writeFile } from 'xlsx';
import AGDataGrid from '@/components/ag-data-grid';
import { useBreadcrumb } from '@/components/breadcrumb-provider';
import Container from '@/components/container';
import { CreateTaskDialog } from '@/components/create-task-dialog';
import { OverrideCell } from '@/components/monitor/override-cell';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useGrid } from '@/contexts/grid-context';
import { isInRegion, REGIONS } from '@/lib/region-utils';
import type { Region } from '@/lib/region-utils';
import { cn } from '@/lib/utils';
import { api } from '../../../../convex/_generated/api';

// ============================================================================
// Types
// ============================================================================

type MonitorStatus = 'live' | 'parked' | 'redirect_in_zone' | 'redirect_out_of_zone' | 'error';

interface ScanHistoryEntry {
  status: string;
  scannedAt: number;
}

// Context to provide scan history to cell renderers
type ScanHistoryMap = Record<string, ScanHistoryEntry[]>;
const ScanHistoryContext = createContext<ScanHistoryMap>({});

interface MonitorDomainRow {
  domain: string;
  tld: string;
  state: string | null;
  country: string | null;
  registrationDate: number | null;
  liveSince: number | null;
  launchVelocityDays: number | null;
  status: MonitorStatus;
  lastScanStatus: string;
  hasActiveOverride: boolean;
  overrideStatus: MonitorStatus | null;
  lastScanAt: number | null;
  highWaterStatus: MonitorStatus | null;
  graceExpiresAt: number | null;
}

// ============================================================================
// Status Badge Component
// ============================================================================

function StatusBadge({ status }: { status: string }) {
  const getStatusStyle = (status: string) => {
    switch (status.toLowerCase()) {
      case 'live': {
        return 'bg-green-100 text-green-800 hover:bg-green-100';
      }
      case 'parked': {
        return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
      }
      case 'redirect_in_zone': {
        return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      }
      case 'redirect_out_of_zone': {
        return 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100';
      }
      case 'error':
      case 'timeout':
      case 'incomplete': {
        return 'bg-red-100 text-red-800 hover:bg-red-100';
      }
      default: {
        return 'bg-gray-100 text-gray-800 hover:bg-gray-100';
      }
    }
  };

  const formatStatusLabel = (status: string) => {
    return status.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase());
  };

  return (
    <Badge className={getStatusStyle(status)}>
      {formatStatusLabel(status)}
    </Badge>
  );
}

// ============================================================================
// Scan History Visualization - 7-Day Timeline
// ============================================================================

const STATUS_COLORS: Record<string, { bg: string; label: string }> = {
  live: { bg: 'bg-green-500', label: 'Live' },
  parked: { bg: 'bg-slate-400', label: 'Parked' },
  redirect_in_zone: { bg: 'bg-blue-500', label: 'Redirect (In Zone)' },
  redirect_out_of_zone: { bg: 'bg-amber-500', label: 'Redirect (Out of Zone)' },
  error: { bg: 'bg-red-500', label: 'Error' },
  timeout: { bg: 'bg-red-400', label: 'Timeout' },
  incomplete: { bg: 'bg-red-300', label: 'Incomplete' },
};

const STATUS_PRIORITY: Record<string, number> = {
  live: 5,
  redirect_in_zone: 4,
  redirect_out_of_zone: 3,
  parked: 2,
  error: 1,
  timeout: 1,
  incomplete: 1,
};

/** Single day box with its own tooltip */
function DayBox({
  date,
  scans,
  bestStatus,
  isToday,
}: {
  date: Date;
  scans: ScanHistoryEntry[];
  bestStatus: string | null;
  isToday: boolean;
}) {
  const hasScans = scans.length > 0;
  const colorStyle = bestStatus ? STATUS_COLORS[bestStatus]?.bg ?? 'bg-gray-300' : '';
  const statusLabel = bestStatus ? STATUS_COLORS[bestStatus]?.label ?? bestStatus : null;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={100}>
        <TooltipTrigger asChild>
          <div
            className={`h-5 w-4 ${
              hasScans
                ? colorStyle
                : 'border border-dashed border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-900'
            } ${isToday ? 'ring-1 ring-foreground/30' : ''}`}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <div className="font-medium">{format(date, 'EEE, MMM d')}</div>
          {hasScans
            ? (
                <>
                  <div className="text-muted-foreground">{statusLabel}</div>
                  {scans.length > 1 && (
                    <div className="text-muted-foreground">
                      {scans.length}
                      {' '}
                      scans
                    </div>
                  )}
                </>
              )
            : (
                <div className="text-muted-foreground">No scan</div>
              )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * 7-Day Timeline - square boxes, each with individual hover
 * Reads scan history from context (fetched for visible rows)
 */
function ScanHistoryCell({
  domain,
  highWaterStatus,
  graceExpiresAt,
  currentStatus,
}: {
  domain: string;
  highWaterStatus: MonitorStatus | null;
  graceExpiresAt: number | null;
  currentStatus: MonitorStatus;
}) {
  const scanHistoryMap = useContext(ScanHistoryContext);
  const scans = scanHistoryMap[domain] || [];

  const DAY_MS = 24 * 60 * 60 * 1000;

  // Get today at midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Build 7-day buckets (index 0 = 6 days ago, index 6 = today)
  const dayBuckets: Array<{ date: Date; scans: ScanHistoryEntry[]; bestStatus: string | null }> = [];
  for (let i = 6; i >= 0; i--) {
    const dayDate = new Date(today);
    dayDate.setDate(dayDate.getDate() - i);

    const dayStart = dayDate.getTime();
    const dayEnd = dayStart + DAY_MS;

    const dayScans = scans.filter((s) => s.scannedAt >= dayStart && s.scannedAt < dayEnd);

    // Find best status for this day (highest priority)
    let bestStatus: string | null = null;
    let bestPriority = 0;
    for (const scan of dayScans) {
      const priority = STATUS_PRIORITY[scan.status] ?? 0;
      if (priority > bestPriority) {
        bestPriority = priority;
        bestStatus = scan.status;
      }
    }

    dayBuckets.push({ date: dayDate, scans: dayScans, bestStatus });
  }

  // Grace period status
  const isInGracePeriod = graceExpiresAt && graceExpiresAt > Date.now();
  const isProtected = isInGracePeriod && currentStatus !== highWaterStatus && highWaterStatus;

  // If no history loaded yet, show loading state
  if (!scanHistoryMap[domain]) {
    return (
      <div className="flex h-full items-center">
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 7 }).map((_, idx) => (
            <div
              key={idx}
              className="h-5 w-4 bg-gray-100 dark:bg-gray-800"
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center gap-2">
      {/* 7-day boxes */}
      <div className="flex items-center gap-0.5">
        {dayBuckets.map((bucket, idx) => (
          <DayBox
            key={bucket.date.getTime()}
            date={bucket.date}
            scans={bucket.scans}
            bestStatus={bucket.bestStatus}
            isToday={idx === 6}
          />
        ))}
      </div>

      {/* Grace indicator */}
      {isProtected && (
        <TooltipProvider>
          <Tooltip delayDuration={100}>
            <TooltipTrigger asChild>
              <div className="h-5 border-l border-amber-400 bg-amber-100 px-1.5 text-[10px] font-medium leading-5 text-amber-700 dark:border-amber-600 dark:bg-amber-900/40 dark:text-amber-400">
                GRACE
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <div className="font-medium">Grace Period Active</div>
              <div className="text-muted-foreground">
                Protected at "
                {STATUS_COLORS[highWaterStatus]?.label}
                " until
              </div>
              <div>{format(new Date(graceExpiresAt), 'MMM d, h:mm a')}</div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
}

// ============================================================================
// Velocity Cell Component (Editable)
// ============================================================================

function VelocityCell({
  domain,
  velocityDays,
  onUpdate,
}: {
  domain: string;
  velocityDays: number | null;
  onUpdate: (domain: string, velocity: number | null) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus and select input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const startEditing = () => {
    setEditValue(velocityDays?.toString() ?? '');
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editValue.trim();
    if (trimmed === '') {
      onUpdate(domain, null);
    } else {
      const parsed = Number.parseInt(trimmed, 10);
      if (!Number.isNaN(parsed) && parsed >= 0) {
        onUpdate(domain, parsed);
      }
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex h-full items-center gap-1">
        <input
          ref={inputRef}
          type="number"
          min="0"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSave();
            } else if (e.key === 'Escape') {
              handleCancel();
            }
          }}
          className="h-6 w-14 rounded border border-input bg-background px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <Button
          variant="ghost"
          size="sm"
          className="size-6 p-0 text-green-600 hover:bg-green-100 hover:text-green-700"
          onClick={handleSave}
        >
          <Check className="size-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="size-6 p-0 text-red-600 hover:bg-red-100 hover:text-red-700"
          onClick={handleCancel}
        >
          <X className="size-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="group flex h-full items-center gap-1">
      <span className="text-sm">
        {velocityDays !== null && velocityDays !== undefined ? `${velocityDays}d` : '—'}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="size-5 p-0 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={startEditing}
      >
        <Pencil className="size-3 text-muted-foreground" />
      </Button>
    </div>
  );
}

// ============================================================================
// Live Since Cell Component (Editable)
// ============================================================================

function LiveSinceCell({
  domain,
  liveSince,
  onUpdate,
}: {
  domain: string;
  liveSince: number | null;
  onUpdate: (domain: string, liveSince: number | null) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const startEditing = () => {
    // Convert timestamp to YYYY-MM-DD format for date input (local timezone)
    if (liveSince) {
      const date = new Date(liveSince);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      setEditValue(`${year}-${month}-${day}`);
    } else {
      setEditValue('');
    }
    setIsEditing(true);
  };

  const handleSave = () => {
    if (editValue.trim() === '') {
      onUpdate(domain, null);
    } else {
      // Parse as local date (YYYY-MM-DD) to avoid timezone issues
      const [year, month, day] = editValue.split('-').map(Number);
      const parsed = new Date(year, month - 1, day, 12, 0, 0); // Noon local time
      if (!Number.isNaN(parsed.getTime())) {
        onUpdate(domain, parsed.getTime());
      }
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex h-full items-center gap-1">
        <input
          ref={inputRef}
          type="date"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleSave();
            } else if (e.key === 'Escape') {
              handleCancel();
            }
          }}
          className="h-6 w-28 rounded border border-input bg-background px-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
        />
        <Button
          variant="ghost"
          size="sm"
          className="size-6 p-0 text-green-600 hover:bg-green-100 hover:text-green-700"
          onClick={handleSave}
        >
          <Check className="size-3" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="size-6 p-0 text-red-600 hover:bg-red-100 hover:text-red-700"
          onClick={handleCancel}
        >
          <X className="size-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="group flex h-full items-center gap-1">
      <span className="text-sm">
        {liveSince ? format(new Date(liveSince), 'MMM d, yyyy') : '—'}
      </span>
      <Button
        variant="ghost"
        size="sm"
        className="size-5 p-0 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={startEditing}
      >
        <Pencil className="size-3 text-muted-foreground" />
      </Button>
    </div>
  );
}

// ============================================================================
// Status Filter Configuration (chip-based header, matches Monitor Events page)
// ============================================================================

const MONITOR_STATUS_ORDER: MonitorStatus[] = ['live', 'parked', 'redirect_in_zone', 'redirect_out_of_zone', 'error'];

const MONITOR_STATUS_CONFIG: Record<MonitorStatus, {
  label: string;
  icon: React.ElementType;
  className: string;
  description: string;
}> = {
  live: {
    label: 'Live',
    icon: CheckCircle2,
    className: 'bg-green-100 text-green-800 hover:bg-green-100',
    description: 'Active websites',
  },
  parked: {
    label: 'Parked',
    icon: ParkingSquare,
    className: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
    description: 'Parked domains',
  },
  redirect_in_zone: {
    label: 'Redirect (In Zone)',
    icon: ArrowRightLeft,
    className: 'bg-blue-100 text-blue-800 hover:bg-blue-100',
    description: 'Redirects to .Bank / .Insurance',
  },
  redirect_out_of_zone: {
    label: 'Redirect (Out)',
    icon: AlertTriangle,
    className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
    description: 'Redirects outside the zone',
  },
  error: {
    label: 'Error',
    icon: XCircle,
    className: 'bg-red-100 text-red-800 hover:bg-red-100',
    description: 'Failed to scan',
  },
};

// Contextual explainer banners shown when a status filter is selected
const STATUS_BANNERS: Partial<Record<MonitorStatus, {
  cardClass: string;
  textClass: string;
  title: string;
  description: string;
}>> = {
  redirect_out_of_zone: {
    cardClass: 'border-amber-200 bg-amber-50',
    textClass: 'text-amber-800',
    title: 'Out-of-Zone Redirects:',
    description: 'These domains redirect to URLs outside .Bank or .Insurance zones.',
  },
  error: {
    cardClass: 'border-red-200 bg-red-50',
    textClass: 'text-red-800',
    title: 'Scan Errors:',
    description: 'These domains encountered errors during scanning. They will be retried on the next scan run.',
  },
};

const TLD_OPTIONS: { value: string; label: string }[] = [
  { value: 'bank', label: '.Bank' },
  { value: 'insurance', label: '.Insurance' },
];

// Toggle a value in/out of a selection array
function toggleValue<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

// ============================================================================
// Main Component
// ============================================================================

function MonitorPage() {
  const [selectedStatuses, setSelectedStatuses] = useState<MonitorStatus[]>([]);
  const [selectedTlds, setSelectedTlds] = useState<string[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<Region[]>([]);
  const [overrideOnly, setOverrideOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedCount, setCopiedCount] = useState(false);
  const [visibleDomains, setVisibleDomains] = useState<string[]>([]);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [taskDialogData, setTaskDialogData] = useState<{ domain: string; status: string } | null>(null);
  const gridApiRef = useRef<GridApi<MonitorDomainRow> | null>(null);
  const { registerGridApi } = useGrid();
  const { setLabel, removeLabel } = useBreadcrumb();

  // Set custom breadcrumb label
  useEffect(() => {
    const path = '/app/monitor';
    setLabel(path, { name: 'Domain Monitor', path });
    return () => removeLabel(path);
  }, [setLabel, removeLabel]);

  // Query Convex for monitor status data (includes state/country/registrationDate from Hasura cache)
  const domainStatuses = useQuery(api.monitor.getAllDomainStatuses, { onlyMonitored: true });
  const overrides = useQuery(api.monitor.getAllActiveOverrides);

  // Query scan history for visible domains only
  const scanHistory = useQuery(
    api.monitor.getScanHistoryForDomains,
    visibleDomains.length > 0 ? { domains: visibleDomains } : 'skip',
  );
  const scanHistoryMap: ScanHistoryMap = scanHistory ?? {};

  // Mutations for overrides, velocity, and liveSince
  const upsertOverride = useMutation(api.monitor.upsertStatusOverride);
  const deactivateOverride = useMutation(api.monitor.deactivateOverride);
  const updateVelocity = useMutation(api.monitor.updateLaunchVelocity);
  const updateLiveSinceMutation = useMutation(api.monitor.updateLiveSince);

  const isLoading = !domainStatuses;

  const overrideMap = useMemo(() => {
    const map = new Map<string, MonitorStatus>();
    if (overrides) {
      for (const o of overrides) {
        map.set(o.domain, o.statusOverride as MonitorStatus);
      }
    }
    return map;
  }, [overrides]);

  // Override handlers
  const handleSetOverride = useCallback(async (domain: string, status: MonitorStatus) => {
    try {
      await upsertOverride({ domain, statusOverride: status, active: true });
      toast.success(`Override set to "${status.replaceAll('_', ' ')}" for ${domain}`);
    } catch {
      toast.error('Failed to set override');
    }
  }, [upsertOverride]);

  const handleClearOverride = useCallback(async (domain: string) => {
    try {
      await deactivateOverride({ domain });
      toast.success(`Override cleared for ${domain}`);
    } catch {
      toast.error('Failed to clear override');
    }
  }, [deactivateOverride]);

  // Velocity update handler
  const handleUpdateVelocity = useCallback(async (domain: string, velocity: number | null) => {
    try {
      await updateVelocity({ domain, launchVelocityDays: velocity });
      toast.success(`Launch velocity updated for ${domain}`);
    } catch {
      toast.error('Failed to update launch velocity');
    }
  }, [updateVelocity]);

  // Live since update handler
  const handleUpdateLiveSince = useCallback(async (domain: string, liveSince: number | null) => {
    try {
      await updateLiveSinceMutation({ domain, liveSince });
      toast.success(`Live since date updated for ${domain}`);
    } catch {
      toast.error('Failed to update live since date');
    }
  }, [updateLiveSinceMutation]);

  // Task dialog handler
  const handleOpenTaskDialog = useCallback((domain: string, status: string) => {
    setTaskDialogData({ domain, status });
    setTaskDialogOpen(true);
  }, []);

  // Build column definitions with override handlers
  const monitorColumnDefs = useMemo((): ColDef<MonitorDomainRow>[] => [
    {
      field: 'domain',
      headerName: 'Domain',
      flex: 0,
      minWidth: 180,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<MonitorDomainRow>) => (
        <Link
          to={`/app/domains/${params.value}`}
          className="text-primary hover:underline"
        >
          {params.value}
        </Link>
      ),
    },
    {
      field: 'tld',
      headerName: 'TLD',
      flex: 0,
      minWidth: 90,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (!params.value)
          return '—';
        return `.${params.value.charAt(0).toUpperCase()}${params.value.slice(1).toLowerCase()}`;
      },
    },
    {
      field: 'state',
      headerName: 'State',
      flex: 0,
      minWidth: 80,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'country',
      headerName: 'Country',
      flex: 0,
      minWidth: 80,
      sortable: true,
      filter: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'registrationDate',
      headerName: 'Reg. Date',
      flex: 0,
      minWidth: 100,
      sortable: true,
      filter: true,
      valueFormatter: (params) => {
        if (!params.value)
          return '—';
        return format(new Date(params.value), 'MMM d, yyyy');
      },
    },
    {
      field: 'liveSince',
      headerName: 'Live Since',
      flex: 0,
      minWidth: 140,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<MonitorDomainRow>) => (
        <LiveSinceCell
          domain={params.data?.domain || ''}
          liveSince={params.data?.liveSince ?? null}
          onUpdate={handleUpdateLiveSince}
        />
      ),
    },
    {
      field: 'launchVelocityDays',
      headerName: 'Velocity',
      flex: 0,
      minWidth: 100,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<MonitorDomainRow>) => (
        <VelocityCell
          domain={params.data?.domain || ''}
          velocityDays={params.data?.launchVelocityDays ?? null}
          onUpdate={handleUpdateVelocity}
        />
      ),
    },
    {
      field: 'domain',
      headerName: '7-Day History',
      colId: 'history',
      flex: 0,
      minWidth: 160,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<MonitorDomainRow>) => (
        <ScanHistoryCell
          domain={params.data?.domain || ''}
          highWaterStatus={params.data?.highWaterStatus ?? null}
          graceExpiresAt={params.data?.graceExpiresAt ?? null}
          currentStatus={params.data?.status || 'error'}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0,
      minWidth: 200,
      sortable: true,
      filter: true,
      cellRenderer: (params: ICellRendererParams<MonitorDomainRow>) => (
        <div className="flex h-full items-center gap-2">
          <StatusBadge status={params.value} />
          <a
            href={`https://${params.data?.domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            <span>Visit</span>
            <ExternalLink className="size-3" />
          </a>
        </div>
      ),
    },
    {
      // Keyed on the status (not the boolean) so AG Grid sees a value change and
      // repaints the cell when an override switches from one status to another.
      field: 'overrideStatus',
      headerName: 'Override',
      flex: 0,
      minWidth: 130,
      sortable: true,
      filter: true,
      valueFormatter: (p) => (p.value ? String(p.value).replaceAll('_', ' ') : 'None'),
      cellRenderer: (params: ICellRendererParams<MonitorDomainRow>) => (
        <OverrideCell
          domain={params.data?.domain || ''}
          hasOverride={params.data?.hasActiveOverride || false}
          overrideStatus={params.data?.overrideStatus ?? null}
          onSetOverride={handleSetOverride}
          onClearOverride={handleClearOverride}
        />
      ),
    },
    {
      headerName: 'Task',
      flex: 0,
      minWidth: 80,
      sortable: false,
      filter: false,
      cellRenderer: (params: ICellRendererParams<MonitorDomainRow>) => {
        if (!params.data)
          return null;
        return (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 px-2 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenTaskDialog(params.data!.domain, params.data!.status);
            }}
          >
            <ListTodo className="size-4" />
            <span className="text-xs">Add</span>
          </Button>
        );
      },
    },
  ], [handleSetOverride, handleClearOverride, handleUpdateVelocity, handleUpdateLiveSince, handleOpenTaskDialog]);

  // Build all rows (state/country/registrationDate come directly from domainStatuses now)
  const allRows = useMemo(() => {
    if (!domainStatuses)
      return [];
    const now = Date.now();

    return domainStatuses.map((d): MonitorDomainRow => {
      const tld = d.domain.split('.').pop() || '';
      const overrideStatus = overrideMap.get(d.domain) ?? null;
      // Derive hasActiveOverride from the overrides query, not the cached flag
      const hasActiveOverride = overrideMap.has(d.domain);

      // Compute effective status live:
      // 1. Override takes precedence
      // 2. If in grace period, use high water mark
      // 3. Otherwise use last scan status
      let effectiveStatus: MonitorStatus = d.lastScanStatus as MonitorStatus;
      if (hasActiveOverride && overrideStatus) {
        effectiveStatus = overrideStatus;
      } else if (d.graceExpiresAt && d.graceExpiresAt > now && d.highWaterStatus) {
        effectiveStatus = d.highWaterStatus as MonitorStatus;
      }

      return {
        domain: d.domain,
        tld,
        state: d.state,
        country: d.country,
        registrationDate: d.registrationDate,
        liveSince: d.liveSince ?? null,
        launchVelocityDays: d.launchVelocityDays
          ?? (d.liveSince && d.registrationDate
            ? Math.floor((d.liveSince - d.registrationDate) / (1000 * 60 * 60 * 24))
            : null),
        status: effectiveStatus,
        lastScanStatus: d.lastScanStatus,
        hasActiveOverride,
        overrideStatus,
        lastScanAt: d.lastScanAt ?? null,
        highWaterStatus: d.highWaterStatus as MonitorStatus | null,
        graceExpiresAt: d.graceExpiresAt ?? null,
      };
    });
  }, [domainStatuses, overrideMap]);

  // Apply the status, TLD, and region chip selections (values within a facet
  // are OR'd; facets are AND'd with each other and the search box)
  const filteredRows = useMemo(() => {
    let rows = allRows;

    if (selectedStatuses.length > 0) {
      const statuses = new Set<string>(selectedStatuses);
      rows = rows.filter((r) => statuses.has(r.status));
    }

    if (selectedTlds.length > 0) {
      const tlds = new Set(selectedTlds);
      rows = rows.filter((r) => tlds.has(r.tld));
    }

    if (selectedRegions.length > 0) {
      rows = rows.filter((r) => r.country && selectedRegions.some((region) => isInRegion(r.country, region)));
    }

    if (overrideOnly) {
      rows = rows.filter((r) => r.hasActiveOverride);
    }

    return rows;
  }, [allRows, selectedStatuses, selectedTlds, selectedRegions, overrideOnly]);

  // Facet counts derived from the full set (independent of facet selection),
  // using the live effective status rather than the cached one
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of allRows) {
      counts[row.status] = (counts[row.status] || 0) + 1;
    }
    return counts;
  }, [allRows]);

  const tldCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of allRows) {
      counts[row.tld] = (counts[row.tld] || 0) + 1;
    }
    return counts;
  }, [allRows]);

  const regionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of allRows) {
      if (!row.country)
        continue;
      for (const r of REGIONS) {
        if (isInRegion(row.country, r.value)) {
          counts[r.value] = (counts[r.value] || 0) + 1;
        }
      }
    }
    return counts;
  }, [allRows]);

  const overrideCount = useMemo(
    () => allRows.filter((r) => r.hasActiveOverride).length,
    [allRows],
  );

  // Facet chip handlers
  const toggleStatus = useCallback((status: MonitorStatus) => {
    setSelectedStatuses((prev) => toggleValue(prev, status));
  }, []);

  const toggleTld = useCallback((tld: string) => {
    setSelectedTlds((prev) => toggleValue(prev, tld));
  }, []);

  const toggleRegion = useCallback((region: Region) => {
    setSelectedRegions((prev) => toggleValue(prev, region));
  }, []);

  const handleCopyCount = useCallback(() => {
    void navigator.clipboard.writeText(String(filteredRows.length));
    setCopiedCount(true);
    setTimeout(() => setCopiedCount(false), 2000);
  }, [filteredRows.length]);

  // Export the currently filtered domains (chip filters applied, search box not) to Excel
  const handleExportReport = useCallback(() => {
    if (filteredRows.length === 0) {
      toast.error('No domains to export');
      return;
    }

    try {
      const formatStatus = (status: string | null) =>
        status ? status.replaceAll('_', ' ').replaceAll(/\b\w/g, (l) => l.toUpperCase()) : '';

      const exportRows = filteredRows.map((row) => ({
        'Domain': row.domain,
        'TLD': row.tld ? `.${row.tld}` : '',
        'State': row.state ?? '',
        'Country': row.country ?? '',
        'Registration Date': row.registrationDate ? format(new Date(row.registrationDate), 'MM/dd/yyyy') : '',
        'Live Since': row.liveSince ? format(new Date(row.liveSince), 'MM/dd/yyyy') : '',
        'Launch Velocity (days)': row.launchVelocityDays ?? '',
        'Status': formatStatus(row.status),
        'Last Scan Status': formatStatus(row.lastScanStatus),
        'Last Scan': row.lastScanAt ? format(new Date(row.lastScanAt), 'MM/dd/yyyy h:mm a') : '',
        'Override': row.hasActiveOverride ? formatStatus(row.overrideStatus) || 'Yes' : 'No',
      }));

      const wb = utils.book_new();
      const ws = utils.json_to_sheet(exportRows);

      ws['!cols'] = [
        { wch: 28 }, // Domain
        { wch: 11 }, // TLD
        { wch: 8 }, // State
        { wch: 10 }, // Country
        { wch: 16 }, // Registration Date
        { wch: 12 }, // Live Since
        { wch: 20 }, // Launch Velocity
        { wch: 20 }, // Status
        { wch: 20 }, // Last Scan Status
        { wch: 18 }, // Last Scan
        { wch: 20 }, // Override
      ];

      utils.book_append_sheet(wb, ws, 'Domain Monitor');

      const statusLabel = selectedStatuses.length > 0 ? `_${selectedStatuses.join('_')}` : '';
      const filename = `domain_monitor${statusLabel}_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;

      writeFile(wb, filename);
      toast.success(`Exported ${exportRows.length} domains to ${filename}`);
    } catch {
      toast.error('Failed to export report');
    }
  }, [filteredRows, selectedStatuses]);

  // Initialize visible domains with first 50 domains when data loads
  useEffect(() => {
    if (domainStatuses && domainStatuses.length > 0 && visibleDomains.length === 0) {
      const initialDomains = domainStatuses.slice(0, 50).map((d) => d.domain);
      setVisibleDomains(initialDomains);
    }
  }, [domainStatuses, visibleDomains.length]);

  // Debounced handler to update visible domains when viewport changes
  const updateVisibleDomains = useDebouncedCallback(() => {
    if (!gridApiRef.current)
      return;

    const renderedNodes: string[] = [];
    gridApiRef.current.forEachNodeAfterFilterAndSort((node) => {
      if (node.data && node.rowIndex !== null) {
        // Get first and last visible row indices
        const firstRow = gridApiRef.current!.getFirstDisplayedRowIndex();
        const lastRow = gridApiRef.current!.getLastDisplayedRowIndex();
        if (node.rowIndex >= firstRow && node.rowIndex <= lastRow) {
          renderedNodes.push(node.data.domain);
        }
      }
    });

    // Only update if different to avoid unnecessary re-renders
    setVisibleDomains((prev) => {
      const newDomains = renderedNodes.slice(0, 50); // Limit to 50 domains
      if (JSON.stringify(prev) !== JSON.stringify(newDomains)) {
        return newDomains;
      }
      return prev;
    });
  }, 150);

  const handleGridReady = (params: { api: GridApi<MonitorDomainRow> }) => {
    gridApiRef.current = params.api;
    registerGridApi(params.api);
    // Initial load of visible domains after grid renders
    setTimeout(updateVisibleDomains, 200);
  };

  const handleViewportChanged = useCallback(() => {
    updateVisibleDomains();
  }, [updateVisibleDomains]);

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedStatuses([]);
    setSelectedTlds([]);
    setSelectedRegions([]);
    setOverrideOnly(false);
    if (gridApiRef.current) {
      gridApiRef.current.setFilterModel(null);
    }
  };

  const hasActiveFilters
    = selectedStatuses.length > 0
      || selectedTlds.length > 0
      || selectedRegions.length > 0
      || overrideOnly
      || searchQuery.trim() !== '';

  // Help AG Grid track row identity for proper updates
  const getRowId = useCallback((params: { data: MonitorDomainRow }) => params.data.domain, []);

  // Update visible domains when the filtered set changes or data loads
  useEffect(() => {
    if (gridApiRef.current && filteredRows.length > 0) {
      setTimeout(updateVisibleDomains, 100);
    }
  }, [filteredRows, updateVisibleDomains]);

  return (
    <Container className="pb-12">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Domain Monitor</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportReport}
          disabled={isLoading || filteredRows.length === 0}
          className="gap-2"
        >
          <FileSpreadsheet className="size-4" />
          Export
          {' '}
          {filteredRows.length.toLocaleString()}
          {' '}
          domains
        </Button>
      </div>

      {/* Loading state */}
      {isLoading && (
        <Card className="mb-6">
          <CardContent className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
              <span>Loading monitor data...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      {!isLoading && (
        <Card className="mb-6 border-dashed">
          <CardContent className="py-4">
            <div className="flex flex-col gap-4">
              {/* Filter groups */}
              <div className="flex flex-wrap gap-x-8 gap-y-4">
                {/* Status */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Status
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => setSelectedStatuses([])}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                        selectedStatuses.length === 0
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
                      )}
                    >
                      All
                      <span className="tabular-nums opacity-70">{allRows.length}</span>
                    </button>
                    {MONITOR_STATUS_ORDER.map((status) => {
                      const config = MONITOR_STATUS_CONFIG[status];
                      const Icon = config.icon;
                      const isSelected = selectedStatuses.includes(status);
                      return (
                        <button
                          key={status}
                          type="button"
                          onClick={() => toggleStatus(status)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border border-transparent px-2.5 py-1 text-xs font-medium transition-all',
                            config.className,
                            isSelected
                              ? 'ring-2 ring-primary/50 ring-offset-1'
                              : 'opacity-60 hover:opacity-100',
                          )}
                          title={config.description}
                        >
                          <Icon className="size-3" />
                          {config.label}
                          <span className="tabular-nums opacity-70">{statusCounts[status] || 0}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* TLD */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    TLD
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {TLD_OPTIONS.map((opt) => {
                      const isSelected = selectedTlds.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleTld(opt.value)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                            isSelected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
                          )}
                        >
                          {opt.label}
                          <span className="tabular-nums opacity-70">{tldCounts[opt.value] || 0}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Region */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Region
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    {REGIONS.map((opt) => {
                      const isSelected = selectedRegions.includes(opt.value);
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => toggleRegion(opt.value)}
                          className={cn(
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all',
                            isSelected
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-border bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground',
                          )}
                        >
                          {opt.label}
                          <span className="tabular-nums opacity-70">{regionCounts[opt.value] || 0}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Override */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                    Override
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    <button
                      type="button"
                      onClick={() => setOverrideOnly((prev) => !prev)}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-full border border-transparent px-2.5 py-1 text-xs font-medium transition-all',
                        'bg-purple-100 text-purple-800 hover:bg-purple-100',
                        overrideOnly
                          ? 'ring-2 ring-primary/50 ring-offset-1'
                          : 'opacity-60 hover:opacity-100',
                      )}
                      title="Domains with an active manual status override"
                    >
                      <ShieldCheck className="size-3" />
                      Has Override
                      <span className="tabular-nums opacity-70">{overrideCount}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* Controls bar */}
              <div className="flex items-center justify-between gap-3 border-t pt-3">
                <div className="flex items-center gap-2">
                  {hasActiveFilters
                    ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearFilters}
                          className="gap-1.5 text-xs text-muted-foreground"
                        >
                          <RotateCcw className="size-3" />
                          Clear filters
                        </Button>
                      )
                    : (
                        <span className="text-xs text-muted-foreground">
                          Click a status, TLD, region, or override chip to filter, or search below
                        </span>
                      )}
                </div>

                <div className="flex items-center gap-3">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          type="button"
                          onClick={handleCopyCount}
                          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
                        >
                          {copiedCount
                            ? <Check className="size-3.5 text-green-600" />
                            : <Copy className="size-3.5" />}
                          <span className="font-mono font-medium">{filteredRows.length.toLocaleString()}</span>
                          <span>domains</span>
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Click to copy count</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <div className="relative w-56">
                    <Input
                      type="search"
                      placeholder="Search domains..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="h-8 pl-8 text-xs"
                    />
                    <Filter className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contextual banners for selected statuses */}
      {!isLoading && selectedStatuses.map((status) => {
        const banner = STATUS_BANNERS[status];
        if (!banner)
          return null;
        return (
          <Card key={status} className={cn('mb-4', banner.cardClass)}>
            <CardContent className="py-3">
              <p className={cn('text-sm', banner.textClass)}>
                <strong>{banner.title}</strong>
                {' '}
                {banner.description}
              </p>
            </CardContent>
          </Card>
        );
      })}

      {/* Data Grid */}
      {!isLoading && (
        <ScanHistoryContext.Provider value={scanHistoryMap}>
          <AGDataGrid
            columnDefs={monitorColumnDefs}
            rowData={filteredRows}
            quickFilterText={searchQuery}
            getRowId={getRowId}
            onGridReady={handleGridReady}
            onViewportChanged={handleViewportChanged}
            onFilterChanged={handleViewportChanged}
            onSortChanged={handleViewportChanged}
            overlayNoRowsTemplate='<span class="ag-overlay-no-rows-center">No domains found for the selected filters</span>'
          />
        </ScanHistoryContext.Provider>
      )}

      {/* Task Creation Dialog */}
      {taskDialogData && (
        <CreateTaskDialog
          open={taskDialogOpen}
          onOpenChange={setTaskDialogOpen}
          domain={taskDialogData.domain}
          status={taskDialogData.status}
          context="monitor"
        />
      )}
    </Container>
  );
}

export default MonitorPage;
